// ====================================================
// src/components/block/liveStreamManagement/videoEffects/compositeFrame.ts
//
// Peinture d'une frame traitée (structure de l'effet actif, puis niveaux vidéo
// en passe finale) — UNE SEULE implémentation, utilisée à l'identique par la
// préview de SourceConfigModal et par VideoEffectsPipelineManager (le flux
// réellement publié aux mini-vues/flux final). Ce que l'opérateur voit en
// réglant est donc exactement ce qui part en aval — pas deux rendus qui
// pourraient diverger.
// ====================================================

import { VideoEffect, blurPersonCutout } from './VideoEffect';
import { FaceTrackingResult } from './FaceTracker';
import { HandTrackingResult } from './HandTracker';

export interface VideoLevels {
  luminosity: number;
  contrast: number;
  /** Température de couleur, en kelvin — PAS un 0-100 abstrait. */
  whiteBalance: number;
}

// Plage volontairement resserrée sur ce qui sert vraiment en éclairage de plateau
// (tungstène ~3000K → ombre/couvert ~8000K), avec un milieu EXACT à 5500K (lumière du
// jour neutre, la référence photo standard) — (3000+8000)/2 = 5500, pas une coïncidence.
export const WB_KELVIN_MIN     = 3000;
export const WB_KELVIN_MAX     = 8000;
export const WB_KELVIN_DEFAULT = 5500;
// Atténuation maximale du canal opposé aux deux bornes — cf. kelvinToTintGain.
const WB_TINT_STRENGTH = 0.35;

export const DEFAULT_VIDEO_LEVELS: VideoLevels = { luminosity: 50, contrast: 50, whiteBalance: WB_KELVIN_DEFAULT };

export function levelsAreDefault(levels: VideoLevels): boolean {
  return levels.luminosity === DEFAULT_VIDEO_LEVELS.luminosity
      && levels.contrast === DEFAULT_VIDEO_LEVELS.contrast
      && levels.whiteBalance === DEFAULT_VIDEO_LEVELS.whiteBalance;
}

export function buildLevelsCssFilter(levels: VideoLevels): string {
  return [
    `brightness(${levels.luminosity / 50})`,
    `contrast(${levels.contrast / 50})`,
  ].join(' ');
}

/**
 * Gain à appliquer sur les canaux rouge/bleu pour simuler la température choisie.
 * Un blend `multiply` (cf. paintProcessedFrame) ne peut que RÉDUIRE un canal — jamais
 * l'amplifier au-delà de 1. On simule donc "plus chaud"/"plus froid" en atténuant le
 * canal opposé plutôt qu'en boostant le canal visé : moins de bleu = plus chaud, moins
 * de rouge = plus froid. Exactement neutre (1, 1) à WB_KELVIN_DEFAULT.
 */
export function kelvinToTintGain(kelvin: number): { redGain: number; blueGain: number } {
  const delta = (kelvin - WB_KELVIN_DEFAULT) / (WB_KELVIN_DEFAULT - WB_KELVIN_MIN); // -1 (min) .. 0 .. +1 (max)
  const clamped = Math.max(-1, Math.min(1, delta));
  return clamped < 0
    ? { redGain: 1, blueGain: 1 + clamped * WB_TINT_STRENGTH }   // plus chaud : moins de bleu
    : { redGain: 1 - clamped * WB_TINT_STRENGTH, blueGain: 1 };  // plus froid : moins de rouge
}

/** Un effet actif, avec sa config résolue — cf. ActiveVideoEffects. */
export interface ActiveEffectEntry {
  effect: VideoEffect<any>;
  config: unknown;
}

export interface PaintFrameArgs {
  outputCtx: CanvasRenderingContext2D;
  /** Canvas de travail où les effets actifs (ou un simple passthrough) dessinent leur
   * structure AVANT l'application des niveaux — un effet qui pose son propre ctx.filter
   * (le flou, p.ex.) ne doit pas se faire écraser par le filtre de niveaux, ni l'inverse. */
  structureCanvas: HTMLCanvasElement;
  width: number;
  height: number;
  frame: HTMLVideoElement;
  /** Au plus un effet par groupe (cf. VideoEffectGroup — l'exclusivité mutuelle au sein
   * d'un groupe est garantie par l'appelant), dans n'importe quel ordre : la composition
   * ci-dessous les trie elle-même par groupe, "surroundings" (la base) avant "face" (le
   * calque posé dessus). */
  activeEffects: ActiveEffectEntry[];
  levels: VideoLevels;
  personAlpha: HTMLCanvasElement | null;
  faceLandmarks: FaceTrackingResult[];
  handLandmarks: HandTrackingResult[];
  scratch: HTMLCanvasElement;
}

export function paintProcessedFrame({
  outputCtx, structureCanvas, width, height, frame,
  activeEffects, levels, personAlpha, faceLandmarks, handLandmarks, scratch,
}: PaintFrameArgs): void {
  if (structureCanvas.width !== width || structureCanvas.height !== height) {
    structureCanvas.width = width;
    structureCanvas.height = height;
  }
  const structureCtx = structureCanvas.getContext('2d');
  if (!structureCtx) return;
  structureCtx.filter = 'none';

  // Calculé AVANT la base : un effet "face" (ex. Avatar) qui remplace la personne par
  // un modèle 3D doit pouvoir dire à l'effet "surroundings" de ne PAS coller la
  // personne par-dessus son fond (cf. VideoEffect.wantsPersonHidden) — sinon ce fond
  // serait déjà recouvert, irrécupérable, quand le calque "face" se dessinerait après.
  const face = activeEffects.find(a => a.effect.group === 'face');
  const hidePerson = !!personAlpha && !!face && face.effect.wantsPersonHidden(face.config);

  // 1. Base — l'effet actif du groupe "surroundings" (Arrière-plan/Flou) s'il y en a un,
  // sinon un simple passthrough. Toujours opaque : c'est sur cette base que le calque
  // "face" se posera ensuite, jamais sur du vide.
  const surroundings = activeEffects.find(a => a.effect.group === 'surroundings');
  if (surroundings) {
    surroundings.effect.composite({ ctx: structureCtx, width, height, frame, personAlpha, faceLandmarks, handLandmarks, hidePerson, scratch, config: surroundings.config });
  } else {
    structureCtx.drawImage(frame, 0, 0, width, height);
    // Pas d'effet "Arrière-plan" actif pour fournir un fond de remplacement à révéler
    // à la place de la personne — à défaut de mieux, on la floute plutôt que de la
    // laisser nette (cf. blurPersonCutout).
    if (hidePerson && personAlpha) blurPersonCutout(structureCtx, frame, personAlpha, width, height, scratch);
  }

  // 2. Calque — l'effet actif du groupe "face" (Makeup/Avatar) s'il y en a un, posé
  // par-dessus la base sans la repeindre (cf. le contrat de VideoEffect.composite()).
  if (face) {
    face.effect.composite({ ctx: structureCtx, width, height, frame, personAlpha, faceLandmarks, handLandmarks, hidePerson, scratch, config: face.config });
  }

  // Passe finale : les niveaux s'appliquent sur le résultat déjà composé (personne
  // détourée comprise), jamais seulement sur le fond ou seulement sur le sujet.
  outputCtx.filter = buildLevelsCssFilter(levels);
  outputCtx.drawImage(structureCanvas, 0, 0, width, height);
  outputCtx.filter = 'none';

  // Balance des blancs : teinte multiplicative (cf. kelvinToTintGain), à part du filter
  // CSS ci-dessus — ni brightness/contrast/hue-rotate ne peuvent exprimer un gain par
  // canal. Sautée à la valeur neutre (5500K) : un rect à (255,255,255) en 'multiply' ne
  // changerait rien, autant éviter le draw call.
  const { redGain, blueGain } = kelvinToTintGain(levels.whiteBalance);
  if (redGain !== 1 || blueGain !== 1) {
    outputCtx.globalCompositeOperation = 'multiply';
    outputCtx.fillStyle = `rgb(${Math.round(redGain * 255)}, 255, ${Math.round(blueGain * 255)})`;
    outputCtx.fillRect(0, 0, width, height);
    outputCtx.globalCompositeOperation = 'source-over';
  }
}
